import { useGetDailySurveysQuery } from 'store/api/pkd/endpoints/dailySurvey';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { TaskStatus } from 'utils/enum';
import { useUpdateParticipantTaskMutation } from 'store/api/pkd/endpoints/participantTask';
import useParticipantTask from 'hooks/useParticipantTask';
import { useEffect, useMemo, useRef } from 'react';
import { getAttribute } from 'utils/participantTask';
import { spinner } from 'utils/string';

const useDailySurvey = ({ onSubmit, onSubmitError }) => {
  const { personAccountProfileId } = useGetPersonAndAttributes();

  const {
    dailySurvey,
    isDailySurveyLoading,
    isDailySurveySuccess,
    status,
    refetch: refetchDailySurvey,
  } = useGetDailySurveysQuery(
    { personAccountProfileId },
    {
      skip             : !personAccountProfileId,
      selectFromResult : ({ data, isFetching, isSuccess }) => {
        const dailySurvey = data?.[0]?.data;

        return {
          dailySurvey          : dailySurvey,
          status               : dailySurvey?.status,
          isDailySurveyLoading : isFetching,
          isDailySurveySuccess : isSuccess,
        };
      },
    }
  );

  const { form, isTaskDetailsLoading } = useParticipantTask({
    personAccountProfileId,
    participantTaskId : dailySurvey?.id,
  });

  const [updateParticipantTask, { isLoading: isSavingDailySurveyEntry }] =
    useUpdateParticipantTaskMutation();

  const creatingDailySurveyEntryRef = useRef(false);

  const handleSubmit = async ({ data }) => {
    if (!creatingDailySurveyEntryRef.current) {
      creatingDailySurveyEntryRef.current = true;
      try {
        await updateParticipantTask({
          entry             : data,
          status            : TaskStatus.Completed,
          participantTaskId : dailySurvey?.id,
        });
        onSubmit();
      } catch {
        onSubmitError();
      }
      creatingDailySurveyEntryRef.current = false;
    }
  };

  const isLoading = isDailySurveyLoading;

  const attributes = dailySurvey?.attributes;
  const startScreenData = useMemo(
    () => getAttribute(attributes, 'meta'),
    [attributes]
  );
  const celebrationScreenData = useMemo(
    () => getAttribute(attributes, 'celebrationScreen'),
    [attributes]
  );

  useEffect(() => {
    const actionBtn = document.getElementsByClassName?.(
      'btn-wizard-submit'
    )?.[0];

    if (actionBtn) {
      if (isSavingDailySurveyEntry) {
        actionBtn.setAttribute('disabled', true);
        actionBtn.classList.add('daily-surveyt-submit-btn');
        actionBtn.innerHTML = `<div style='display: flex; align-items: center; justify-content: center'>Submit${spinner('#fff')}</div>`;
      } else {
        actionBtn.removeAttribute('disabled');
        actionBtn.innerHTML = 'Submit';
      }
    }
  }, [isSavingDailySurveyEntry]);

  return {
    dailySurvey,
    isDailySurveyLoading,
    status,
    isLoading,
    form,
    isTaskDetailsLoading,
    completed : status === TaskStatus.Completed,
    isDailySurveySuccess,
    isSavingDailySurveyEntry,
    celebrationScreenData,
    startScreenData,
    refetchDailySurvey,
    handleSubmit,
  };
};

export default useDailySurvey;
